// ─── Group Xocdia Livestream Modal ────────────────────────────────
// Modal livestream cho group — game Xóc Đĩa.
// Copy UI từ XocdiaLivestreamModal (account) nhưng:
//   - Dùng shared session (useSharedSession) thay vì SSE raw logs
//   - Giữ header member dots (trạng thái hoạt động các thành viên)
//   - Không có console logs / controls (group chỉ xem session)

import React from 'react';
import {
  type GroupLivestreamModalProps,
  parseTypegame,
  gameName,
  statusColorClass,
  statusPingColorClass,
  countdownColorClass,
  sessionStatusColorClass,
  useGroupLivestream,
  GroupMemberDots,
  formatJackpot,
  formatCoin,
} from './group-livestream-base';
import { GroupSessionHistoryTable } from './GroupSessionHistoryTable';

// ─── Component ─────────────────────────────────────────────────────

export const GroupXocdiaLivestreamModal: React.FC<GroupLivestreamModalProps> = ({
  isOpen,
  onClose,
  groupName,
  groupId,
  members,
  gameType,
}) => {
  const { session, overallStatus, runningMember, anyRunning } = useGroupLivestream(
    members,
    isOpen,
    gameType,
  );

  if (!isOpen) return null;

  // Ưu tiên `gameType` của NHÓM (router modal đã resolve): `config` của account
  // có thể rỗng/không parse được — nhóm đã Stop là ca thường gặp — và khi đó bảng
  // "show phiên" nhận typegame '?' rồi in ra eid thô (5, 1, 3) thay vì nhãn cửa.
  const typegame =
    gameType && gameType !== '?' ? gameType : parseTypegame(runningMember?.config);
  const gName = gameName(typegame);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-2xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header + member dots */}
        <div className="mb-4 pr-10">
          <h3 className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-white">
            <span className="relative flex h-3 w-3">
              <span
                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${statusPingColorClass(overallStatus as any)}`}
              />
              <span
                className={`relative inline-flex h-3 w-3 rounded-full ${statusColorClass(overallStatus as any)}`}
              />
            </span>
            Group Livestream — {groupName}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-400">
            <span>
              Trạng thái:{' '}
              <span className="font-bold uppercase text-slate-300">{overallStatus}</span>
            </span>
            <span className="text-slate-600">|</span>
            <span>
              Game:{' '}
              <span className="font-bold text-purple-400">
                {gName} ({typegame})
              </span>
            </span>
            <span className="text-slate-600">|</span>
            <GroupMemberDots members={members} />
          </div>
        </div>

        {/* Session stats grid */}
        <div className="mb-4 grid grid-cols-4 gap-3">
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Phiên
            </div>
            <div className="mt-1 text-lg font-extrabold text-blue-400">
              {session.sessionId ? `#${session.sessionId}` : '---'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Đặt phiên
            </div>
            <div className={`mt-1 text-lg font-extrabold ${countdownColorClass(session)}`}>
              {session.countdown > 0
                ? `${session.countdown}s`
                : session.sessionId
                  ? 'Chờ...'
                  : '---'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Trạng thái
            </div>
            <div
              className={`mt-1 text-sm font-extrabold ${sessionStatusColorClass(session.status)}`}
            >
              {session.status || '---'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Hũ</div>
            <div className="mt-1 text-lg font-extrabold text-amber-400">
              {session.jackpot > 0 ? formatJackpot(session.jackpot) : '---'}
            </div>
          </div>
        </div>

        {/* Coins */}
        {session.coins.length > 0 && (
          <div className="mb-4 mt-4 rounded-lg border border-slate-700 bg-slate-800/50 p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Mệnh giá cược
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {session.coins.map((coin, i) => (
                <span
                  key={i}
                  className="rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-bold text-slate-300"
                >
                  {formatCoin(coin)}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Session history table (show phiên) — thay cho "kết quả gần nhất" */}
        <GroupSessionHistoryTable
          groupId={groupId}
          typegame={typegame}
          members={members}
          isOpen={isOpen}
        />

        {/* Idle notice */}
        {!anyRunning && (
          <div className="mb-4 rounded-lg border border-dashed border-slate-700 bg-slate-800/30 p-4 text-center text-xs font-semibold text-slate-400">
            Chưa có tài khoản nào trong nhóm đang chạy livestream.
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            onClick={onClose}
            className="rounded-lg bg-slate-800 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-slate-700"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};
